import { FacebookRegularIcon as FacebookIcon, InstagramIcon, RedditIcon } from '@change/ds-icons';

import { SocialIcon } from '@change/ds-core-ui/next';

import type { FullPostType, SharingModalFactoryProps } from './types';

type ComponentsFactoryType = {
	iconComponent: () => JSX.Element;
	modalTitle: string;
	modalDescription: string;
	postPreviewComponent: () => JSX.Element;
	nextStepsComponent: () => JSX.Element;
	secondaryCTA: string | undefined;
	primaryCTA: string;
};

// Note: The following constants are exported for testing purposes only. They should not be used outside of this file.

export const ICONS = {
	facebook: () => <SocialIcon icon={<FacebookIcon />} label="facebook" variant="facebook" />,
	instagram: () => <SocialIcon icon={<InstagramIcon />} label="instagram" variant="instagram" />,
	reddit: () => <SocialIcon icon={<RedditIcon />} label="reddit" variant="reddit" />,
};

export const TITLE_COPIES: Record<FullPostType, string> = {
	facebook_post: 'Your Facebook post',
	facebook_story: 'Your Facebook story',
	facebook_group_post: 'Your Facebook post',
	instagram_post: 'Your Instagram post',
	instagram_story: 'Your Instagram story',
	reddit_post: 'Post on Reddit',
	reddit_group_post: 'Post on Reddit',
};
export const DESCRIPTION_COPIES: Record<FullPostType, string> = {
	facebook_post: 'We’ve generated a custom post based on your petition and Facebook best practices. ',
	facebook_group_post: 'We drafted a post about your petition, written to fit the Facebook groups style. ',
	facebook_story: 'We created this Facebook Story to help promote your petition.',
	instagram_post: 'We created an Instagram post that’s based on your petition.',
	instagram_story: 'We created this Instagram Story to help promote your petition.',
	reddit_post: 'We’ve generated a custom post based on your petition and Reddit best practices. ',
	reddit_group_post: 'We’ve generated a custom post based on your petition and Reddit best practices. ',
};

// TODO: Replace for the actual post preview components once implemented
export const POST_PREVIEWS: Record<FullPostType, () => JSX.Element> = {
	facebook_post: () => <div>Facebook post preview</div>,
	facebook_story: () => <div>Facebook story preview</div>,
	facebook_group_post: () => <div>Facebook group post preview</div>,
	instagram_post: () => <div>Instagram post preview</div>,
	instagram_story: () => <div>Instagram story preview</div>,
	reddit_post: () => <div>Reddit post preview</div>,
	reddit_group_post: () => <div>Reddit group post preview</div>,
};

// TODO: Replace for the actual post preview components once implemented
export const NEXT_STEPS: Record<FullPostType, () => JSX.Element> = {
	facebook_post: () => <div>Facebook post next steps</div>,
	facebook_story: () => <div>Facebook story next steps</div>,
	facebook_group_post: () => <div>Facebook group post next steps</div>,
	instagram_post: () => <div>Instagram post next steps</div>,
	instagram_story: () => <div>Instagram story next steps</div>,
	reddit_post: () => <div>Reddit post next steps</div>,
	reddit_group_post: () => <div>Reddit group post next steps</div>,
};

export const SECONDARY_CTAS: Record<FullPostType, string | undefined> = {
	facebook_post: undefined,
	facebook_story: undefined,
	facebook_group_post: 'View group',
	instagram_post: undefined,
	instagram_story: undefined,
	reddit_post: undefined,
	reddit_group_post: 'View subreddit',
};
export const PRIMARY_CTAS: Record<FullPostType, string> = {
	facebook_post: 'Post on Facebook Feed',
	facebook_story: 'Post on Facebook story',
	facebook_group_post: 'Post in group',
	instagram_post: 'Post on Instagram Feed',
	instagram_story: 'Post on Instagram Story',
	reddit_post: 'Post in subreddit',
	reddit_group_post: 'Post in subreddit',
};

export function componentsFactory(props: SharingModalFactoryProps): ComponentsFactoryType {
	let recommendedGroup;
	const { platform, postType } = props;

	if ('recommendedGroup' in props) {
		recommendedGroup = props.recommendedGroup;
	}

	const fullPostType = `${platform}${recommendedGroup ? '_group' : ''}_${postType}` as FullPostType;

	return {
		iconComponent: ICONS[platform],
		modalTitle: TITLE_COPIES[fullPostType],
		modalDescription: DESCRIPTION_COPIES[fullPostType],
		postPreviewComponent: POST_PREVIEWS[fullPostType],
		nextStepsComponent: NEXT_STEPS[fullPostType],
		secondaryCTA: SECONDARY_CTAS[fullPostType],
		primaryCTA: PRIMARY_CTAS[fullPostType],
	};
}
